// src/pages/TicketCategories.jsx
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

const FONT = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600&family=Roboto:wght@300;400;500;700&display=swap');
`;

const LS_KEY = "hrms_ticket_categories_v2";
const uid = (p = "id") => `${p}_${Date.now().toString(36)}_${Math.floor(Math.random() * 99999)}`;

/* ------------------------- Default Data (MOVED HERE) ------------------------- */
// This must be defined before it is used in the useState call.
const DEFAULT_DATA = [
  {
    id: uid("cat"),
    name: "HR Issues",
    description: "Queries related to HR policies and employee concerns",
    subcategories: [
      { id: uid("sub"), name: "Salary Issue" },
      { id: uid("sub"), name: "Leave Query" }
    ]
  },
  {
    id: uid("cat"),
    name: "IT Support",
    description: "Technical issues and software access",
    subcategories: [
      { id: uid("sub"), name: "Email Access" },
      { id: uid("sub"), name: "Laptop Problem" }
    ]
  }
];


/* ------------------------- Modal ------------------------- */
const Modal = ({ open, title, onClose, width = "max-w-md", children }) => {
  if (!open) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center pt-20 px-4"
      >
        <motion.div
          initial={{ scale: 0.94, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.94, opacity: 0 }}
          className={`bg-white w-full ${width} rounded-2xl shadow-2xl max-h-[70vh] overflow-auto`}
        >
          <div className="px-5 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
            <button
              onClick={onClose}
              className="px-3 py-1 text-gray-500 hover:bg-gray-100 rounded"
            >
              ✕
            </button>
          </div>
          <div className="p-5">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

/* --------------------------- Main --------------------------- */

export default function TicketCategories() {
  const [categories, setCategories] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      // DEFAULT_DATA is now available here
      return raw ? JSON.parse(raw) : DEFAULT_DATA; 
    } catch {
      // DEFAULT_DATA is now available here
      return DEFAULT_DATA;
    }
  });

  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState("");

  // DEFAULT_DATA definition has been removed from here

  /* ---------------------- Persist ---------------------- */
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(categories));
  }, [categories]);

  /* ---------------------- Toast ---------------------- */
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 1400);
  };

  /* ---------------------- CRUD ---------------------- */
  const openNewCategory = () => {
    setEditing({
      id: uid("cat"),
      name: "",
      description: "",
      subcategories: [],
    });
    setModalOpen(true);
  };

  const openEditCategory = (cat) => {
    setEditing(JSON.parse(JSON.stringify(cat)));
    setModalOpen(true);
  };

  const saveCategory = (cat) => {
    setCategories((prev) => {
      const exists = prev.find((c) => c.id === cat.id);
      if (!exists) return [cat, ...prev];
      return prev.map((c) => (c.id === cat.id ? cat : c));
    });

    setModalOpen(false);
    showToast("Saved");
  };

  const deleteCategory = (id) => {
    if (!window.confirm("Delete this category?")) return;
    setCategories((prev) => prev.filter((c) => c.id !== id));
    showToast("Deleted");
  };

  /* ---------------------- Import / Export ---------------------- */
  const exportAll = () => {
    const blob = new Blob([JSON.stringify(categories, null, 2)], {
      type: "application/json",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "ticket_categories.json";
    a.click();
  };

  const importFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        const cleaned = parsed.map((c) => ({
          ...c,
          id: c.id || uid("cat"),
          subcategories: (c.subcategories || []).map((s) => ({
            ...s,
            id: s.id || uid("sub"),
          })),
        }));
        setCategories((prev) => [...cleaned, ...prev]);
        showToast("Imported");
      } catch {
        alert("Invalid file");
      }
    };
    reader.readAsText(file);
  };

  /* ---------------------- Filter ---------------------- */
  const filtered = categories.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  /* ---------------------- UI ---------------------- */
  return (
    <div
      className="min-h-screen bg-gray-50 p-6"
      style={{ fontFamily: "Poppins, Roboto, sans-serif" }}
    >
      <style>{FONT}</style>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="fixed top-6 right-6 bg-indigo-600 text-white px-4 py-2 rounded shadow z-[80]"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">
              Ticket Categories
            </h1>
            <p className="text-sm text-gray-500">
              Manage categories & subcategories for the Helpdesk.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg shadow hover:bg-indigo-700"
              onClick={openNewCategory}
            >
              + Add Category
            </button>

            <button
              className="px-4 py-2 bg-white border text-sm rounded-lg"
              onClick={exportAll}
            >
              Export
            </button>

            <label className="px-4 py-2 bg-white border text-sm rounded-lg cursor-pointer">
              Import
              <input
                type="file"
                accept=".json"
                className="hidden"
                onChange={(e) =>
                  e.target.files?.[0] && importFile(e.target.files[0])
                }
              />
            </label>
          </div>
        </div>

        {/* Search */}
        <div className="flex justify-between items-center">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search categories..."
            className="px-4 py-2 w-72 border rounded-lg bg-white shadow-sm"
          />
        </div>

        {/* List */}
        <div className="bg-white shadow rounded-2xl p-5">
          {filtered.length === 0 && (
            <div className="text-center text-gray-500 py-10">
              No categories found.
            </div>
          )}

          <div className="space-y-4">
            {filtered.map((cat) => (
              <div
                key={cat.id}
                className="border rounded-xl p-4 hover:bg-gray-50 transition"
              >
                <div className="flex justify-between">
                  <div>
                    <div className="text-xl font-semibold text-gray-800">
                      {cat.name}
                    </div>
                    <div className="text-sm text-gray-500">{cat.description}</div>

                    {/* Subcategories */}
                    <div className="mt-3">
                      <h4 className="text-xs font-semibold text-gray-600">
                        Subcategories:
                      </h4>
                      {cat.subcategories.length === 0 && (
                        <div className="text-xs text-gray-400 ml-1">None</div>
                      )}

                      <div className="mt-1 flex flex-wrap gap-2">
                        {cat.subcategories.map((sub) => (
                          <span
                            key={sub.id}
                            className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-lg"
                          >
                            {sub.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 items-end">
                    <button
                      onClick={() => openEditCategory(cat)}
                      className="text-sm bg-white border px-3 py-1 rounded hover:bg-gray-100"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteCategory(cat.id)}
                      className="text-sm bg-rose-50 border border-rose-200 text-rose-700 px-3 py-1 rounded hover:bg-rose-100"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        open={modalOpen}
        title={editing?.name || "Category"}
        onClose={() => setModalOpen(false)}
        width="max-w-lg"
      >
        <CategoryForm
          initial={editing}
          onSave={saveCategory}
          onCancel={() => setModalOpen(false)}
        />
      </Modal>
    </div>
  );
}

/* ------------------ Category Form ------------------ */
function CategoryForm({ initial, onSave, onCancel }) {
  const [cat, setCat] = useState(initial);

  const updateField = (patch) => setCat((c) => ({ ...c, ...patch }));

  const updateSub = (idx, val) => {
    const arr = [...cat.subcategories];
    arr[idx].name = val;
    updateField({ subcategories: arr });
  };

  return (
    <div className="space-y-4">
      {/* Form fields */}
      <div>
        <label className="text-xs text-gray-600">Category Name</label>
        <input
          value={cat.name}
          onChange={(e) => updateField({ name: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg"
        />
      </div>

      <div>
        <label className="text-xs text-gray-600">Description</label>
        <textarea
          value={cat.description}
          onChange={(e) => updateField({ description: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg h-20"
        />
      </div>

      {/* Subcategories */}
      <div className="border rounded-lg p-3">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-semibold text-gray-700">Subcategories</h4>
          <button
            onClick={() =>
              updateField({
                subcategories: [
                  ...cat.subcategories,
                  { id: uid("sub"), name: "" },
                ],
              })
            }
            className="px-2 py-1 bg-emerald-600 text-white rounded text-xs"
          >
            + Add
          </button>
        </div>

        <div className="space-y-2">
          {cat.subcategories.map((s, idx) => (
            <div className="flex gap-2 items-center" key={s.id}>
              <input
                value={s.name}
                onChange={(e) => updateSub(idx, e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg"
                placeholder="Subcategory name"
              />
              <button
                onClick={() =>
                  updateField({
                    subcategories: cat.subcategories.filter((_, i) => i !== idx),
                  })
                }
                className="px-2 py-2 bg-rose-50 text-rose-600 rounded"
              >
                Del
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-2 pt-2 border-t">
        <button onClick={onCancel} className="px-4 py-2 bg-white border rounded">
          Cancel
        </button>
        <button
          onClick={() => onSave(cat)}
          className="px-4 py-2 bg-indigo-600 text-white rounded"
        >
          Save
        </button>
      </div>
    </div>
  );
}